import { RecordValueWithSensograms } from '../../services/cache/idb';
import { arraysEqual } from '../../utils/helpers/array';
import { aggregateSensogramSpans, correctHumidity, customMinMaxNormalisation, filterSensorgramSpans, getAggregatedSpotsgridIndicesMap, linearFit, normalizeL2 } from './compute';
import { AggregationMethod, BasicNormalisationMethod, NormalisationMethod, PipelineType, ProcessingPipelineParams, signal, signature } from './definitions';
import { agg, mean, transpose } from './utils';

export const DEFAULT_SIGNATURE_PROCESSING_PARAMS: ProcessingPipelineParams = {
  pipelineType: PipelineType.DOH,
  aggregation: AggregationMethod.Median,
  normalisation: { type: 'basic', method: BasicNormalisationMethod.L2 },
};

export const computeSignature = (record: RecordValueWithSensograms, processingParams?: ProcessingPipelineParams) => {
  if (record.baselineStart === undefined || record.baselineEnd === undefined) {
    throw Error('Baseline bounds are undefined');
  }
  if (record.analyteStart === undefined || record.analyteEnd === undefined) {
    throw Error('Analyte bounds are undefined');
  }
  const baselineBounds = [record.baselineStart, record.baselineEnd];
  const analyteBounds = [record.analyteStart, record.analyteEnd];
  const a = record.sensogramSeries;
  return computeSignatureFromSignal(a, baselineBounds, analyteBounds, record.device.spotsgrid, processingParams);
};

/**
 * Funcion computing signature with the right processing function depending on selected pipeline parameter.
 * Do not hesitate to add more processing options for more pipeline types !
 * Note: any processing function should handle baselineBounds and analyteBounds set to null
 * @param a Sensorgram / array of frames, from which to extract the signature
 * @param baselineBounds An array indicating [baseline start index, baseline stop index]. If null, signature will be computed only on analyte without substracting any baseline
 * @param analyteBounds An array indicating [analyte start index, analyte stop index]. If null, the whole signal a will be considered as analyte signal
 * @param spotgrid1d Corresponding spotgrid for the input signal
 * @param processingParams A set of processing parameters to configure the processing steps to apply when extracting the signature
 * @returns The extracted signature associated to its corresponding spotgrid
 */
export const computeSignatureFromSignal = (a: signal, baselineBounds: number[] | null, analyteBounds: number[] | null, spotgrid1d: number[], processingParams?: ProcessingPipelineParams): [signature, number[]] => {
  if (processingParams === undefined) {
    processingParams = DEFAULT_SIGNATURE_PROCESSING_PARAMS;
  }

  switch (processingParams.pipelineType) {
    case PipelineType.Classic:
      return computeClassicSignatureFromSignal(a, baselineBounds, analyteBounds, spotgrid1d, processingParams);
    case PipelineType.DOH:
      return computeDOHSignatureFromSignal(a, baselineBounds, analyteBounds, spotgrid1d, processingParams);
    case PipelineType.Slope:
      return computeSlopesFromSignal(a, analyteBounds, spotgrid1d, processingParams);
  }
};

const computeDOHSignatureFromSignal = (a: signal, baselineBounds: number[] | null, analyteBounds: number[] | null, spotgrid1d: number[], processingParams: ProcessingPipelineParams): [signature, number[]] => {
  // process analyte, with slicing only if bounds are provided
  let analyteSpotgrid = [...spotgrid1d];
  let analyteSignal: number[][];
  if (analyteBounds !== null) {
    analyteSignal = transpose(a.slice(...analyteBounds));
  } else {
    analyteSignal = transpose(a);
  }
  [analyteSignal, analyteSpotgrid] = filterSensorgramSpans(analyteSignal, analyteSpotgrid);
  if (processingParams.aggregation !== undefined) {
    [analyteSignal, analyteSpotgrid] = aggregateSensogramSpans(analyteSignal, analyteSpotgrid, processingParams.aggregation);
  }
  const analyteMeans = analyteSignal.map((x) => mean(x));

  // process baseline only if bounds are provided
  // otherwise set it to zeroes for below signature substraction
  let baselineMeans: number[];
  let baselineSpotgrid: number[];
  if (baselineBounds !== null) {
    baselineSpotgrid = [...spotgrid1d];
    let baselineSignal = transpose(a.slice(...baselineBounds));
    [baselineSignal, baselineSpotgrid] = filterSensorgramSpans(baselineSignal, baselineSpotgrid);
    if (processingParams.aggregation !== undefined) {
      [baselineSignal, baselineSpotgrid] = aggregateSensogramSpans(baselineSignal, baselineSpotgrid, processingParams.aggregation);
    }
    baselineMeans = baselineSignal.map((x) => mean(x));
  } else {
    baselineMeans = new Array(analyteSpotgrid.length).fill(0);
    baselineSpotgrid = [...analyteSpotgrid];
  }

  // sanity check
  if (!arraysEqual(baselineSpotgrid, analyteSpotgrid)) {
    throw Error(`Spotgrids in baseline and analyte do not correspond: ${baselineSpotgrid} vs ${analyteSpotgrid}`);
  }

  let signature: number[] = [];
  for (let i = 0; i < analyteMeans.length; i++) {
    signature.push(analyteMeans[i] - baselineMeans[i]);
  }

  // TODO add here humidity correction feature, before applying normalisation
  // to implement this, we need to change a bit the humidityCorrection parameter logic
  // as the humidity calibrant needs to receive the same aggregation processing than the signal to process
  if (processingParams.humidityCorrectionParams !== undefined) {
    throw Error(`Error processing signature: unimplemented humidity correction for DOH signature Pipeline`);
  }

  if (processingParams.normalisation !== undefined) {
    signature = handleNormalisation(signature, analyteSpotgrid, processingParams.normalisation);
  }

  [signature, analyteSpotgrid] = sortSignature(analyteSpotgrid, signature);
  return [signature, analyteSpotgrid];
};

const computeClassicSignatureFromSignal = (a: signal, baselineBounds: number[] | null, analyteBounds: number[] | null, spotgrid1d: number[], processingParams: ProcessingPipelineParams): [signature, number[]] => {
  let baselineMeans: number[];
  if (baselineBounds !== null) {
    baselineMeans = transpose(a.slice(...baselineBounds)).map((sig) => mean(sig));
  } else {
    baselineMeans = new Array(spotgrid1d.length).fill(0);
  }

  let analyteMeans: number[];
  if (analyteBounds !== null) {
    analyteMeans = transpose(a.slice(...analyteBounds)).map((sig) => mean(sig));
  } else {
    analyteMeans = transpose(a).map((sig) => mean(sig));
  }

  if (baselineMeans.length !== analyteMeans.length) {
    throw Error(`Number of sensors in baseline and plateau do not correspond: ${baselineMeans.length} vs ${analyteMeans.length}`);
  }
  const signature: number[] = [];
  for (let i = 0; i < analyteMeans.length; i++) {
    signature.push(analyteMeans[i] - baselineMeans[i]);
  }

  return processSignature(signature, spotgrid1d, processingParams);
};

export const computeSlopesFromSignal = (a: signal, analyteBounds: number[] | null, spotgrid1d: number[], processingParams: ProcessingPipelineParams): [signature, number[]] => {
  let analyteSpotgrid = [...spotgrid1d];
  let analyteSignal: number[][];
  if (analyteBounds !== null) {
    analyteSignal = transpose(a.slice(...analyteBounds));
  } else {
    analyteSignal = transpose(a);
  }
  [analyteSignal, analyteSpotgrid] = filterSensorgramSpans(analyteSignal, analyteSpotgrid);
  if (processingParams.aggregation !== undefined) {
    [analyteSignal, analyteSpotgrid] = aggregateSensogramSpans(analyteSignal, analyteSpotgrid, processingParams.aggregation);
  }

  const slopesArray = analyteSignal.map((elt) => parseFloat((100 * linearFit(elt).a).toFixed(2)));
  // const rSquaredArray = sectionMZIsSpans.map(elt => linearFit(elt).rSquared);

  return [slopesArray, analyteSpotgrid];
};

export const processSignature = (signature: signature, spotsgrid1d: number[], signatureProcessingParams: ProcessingPipelineParams): [signature, number[]] => {
  // let's copy to avoid any side effect
  let processedSignature: signature = [...signature];
  let processedSpotsgrid1d: number[] = [...spotsgrid1d];

  if (signatureProcessingParams.humidityCorrectionParams !== undefined) {
    processedSignature = correctHumidity(signature, signatureProcessingParams.humidityCorrectionParams.deltaH, processedSpotsgrid1d, signatureProcessingParams.humidityCorrectionParams.calibrant);
  }

  [processedSignature, processedSpotsgrid1d] = filterSignature(processedSignature, processedSpotsgrid1d);

  if (signatureProcessingParams.aggregation !== undefined) {
    [processedSignature, processedSpotsgrid1d] = aggregateSignature(processedSignature, processedSpotsgrid1d, signatureProcessingParams.aggregation);
  }

  if (signatureProcessingParams.normalisation !== undefined) {
    processedSignature = handleNormalisation(processedSignature, processedSpotsgrid1d, signatureProcessingParams.normalisation);
  }

  let [sortedFinaleSignature, sortedFinalSpotsgrid1d] = sortSignature(processedSpotsgrid1d, processedSignature);

  return [sortedFinaleSignature, sortedFinalSpotsgrid1d];
};

export const aggregateSignature = (signature: signature, spotsgrid1d: number[], aggregationMethod: AggregationMethod): [signature, number[]] => {
  const _aggregationIndicesMap = getAggregatedSpotsgridIndicesMap(spotsgrid1d);
  const aggregatedSignature: signature = [];
  const aggregatedSpotsgrid1d: number[] = [];
  for (const [aggKey, indices] of Object.entries(_aggregationIndicesMap)) {
    aggregatedSignature.push(
      agg(
        indices.map((i) => signature[i]),
        aggregationMethod
      )
    );
    aggregatedSpotsgrid1d.push(parseInt(aggKey));
  }
  return [aggregatedSignature, aggregatedSpotsgrid1d];
};

export const handleNormalisation = (signature: signature, spotgrid: number[], normMethod: NormalisationMethod) => {
  if (normMethod.type === 'basic' && normMethod.method === BasicNormalisationMethod.L2) {
    signature = normalizeL2(signature);
  } else if (normMethod.type === 'customMinMax') {
    signature = customMinMaxNormalisation(signature, spotgrid, normMethod.rules);
  } else {
    throw new Error(`Unhandled noramlisation: ${normMethod}`);
  }
  return signature;
};

/*
Sort each signature in an array in spotName ASC order
*/
export const sortSignature = (spotsgrid1d: number[], signature: signature): [signature, number[]] => {
  const spotsWithValues: [number, number][] = [];
  for (let i = 0; i < signature.length; i++) {
    spotsWithValues.push([spotsgrid1d[i], signature[i]]);
  }
  // console.log('before sort', [...spotsWithValues]);
  spotsWithValues.sort((a, b) => a[0] - b[0]);
  // console.log('after sort', spotsWithValues);
  let sortedSpotsgrid1d = spotsWithValues.map((e) => e[0]);
  let sortedSignature = spotsWithValues.map((e) => e[1]);

  return [sortedSignature, sortedSpotsgrid1d];
};

export const filterSignature = (signature: signature, spotgrid1d: number[]): [signature, number[]] => {
  let filteredSignature: number[] = [];
  let filteredSpotsgrid1d: number[] = [];
  for (let i = 0; i < spotgrid1d.length; i++) {
    let sensorInt = spotgrid1d[i];
    if (sensorInt >= 1) {
      filteredSignature.push(signature[i]);
      filteredSpotsgrid1d.push(sensorInt);
    }
  }
  return [filteredSignature, filteredSpotsgrid1d];
};
